import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import { caseEvidencePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface EvidenceEntry {
  id: number
  evidence_date: string | null
  employee_name: string | null
  case_id: number
  case_name: string | null
  evidence_type: string | null
  description: string
}

interface FilterOption {
  id: number
  name: string
}

interface Filters {
  start_date: string
  end_date: string
  employee_id: string | null
  case_id: string | null
  evidence_type_id: string | null
}

interface FilterOptions {
  employees: FilterOption[]
  cases: FilterOption[]
  evidence_types: FilterOption[]
}

interface Props {
  entries: EvidenceEntry[]
  filters: Filters
  filter_options: FilterOptions
}

/* -- Helpers ---------------------------------------------- */

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

/* -- Constants -------------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Activity", href: "#" },
  { title: "Evidence", href: "/employees/activity/evidence" },
]

/* -- Component -------------------------------------------- */

export default function Evidence({ entries, filters, filter_options }: Props) {
  const [startDate, setStartDate] = useState(filters.start_date)
  const [endDate, setEndDate] = useState(filters.end_date)
  const [employeeId, setEmployeeId] = useState(filters.employee_id ?? "")
  const [caseId, setCaseId] = useState(filters.case_id ?? "")
  const [evidenceTypeId, setEvidenceTypeId] = useState(
    filters.evidence_type_id ?? "",
  )

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    }
    if (employeeId) params.employee_id = employeeId
    if (caseId) params.case_id = caseId
    if (evidenceTypeId) params.evidence_type_id = evidenceTypeId

    router.get("/employees/activity/evidence", params, {
      preserveState: true,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Evidence Log" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Evidence Log</h1>

        {/* Filter Form */}
        <div className="border-hpi-light-gray mb-6 rounded border bg-gray-50 p-4">
          <form
            onSubmit={handleSearch}
            className="flex flex-wrap items-end gap-4"
          >
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                Employee
              </label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              >
                <option value="">All</option>
                {filter_options.employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                Case
              </label>
              <select
                value={caseId}
                onChange={(e) => setCaseId(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              >
                <option value="">All</option>
                {filter_options.cases.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs font-medium uppercase">
                Type
              </label>
              <select
                value={evidenceTypeId}
                onChange={(e) => setEvidenceTypeId(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              >
                <option value="">All</option>
                {filter_options.evidence_types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                type="submit"
                className="bg-hpi-blue rounded px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Results */}
        {entries.length === 0 ? (
          <div className="rounded border border-red-200 bg-red-50 px-6 py-8 text-center">
            <p className="text-lg font-medium text-red-800">
              No evidence found in date range
            </p>
          </div>
        ) : (
          <div className="border-hpi-light-gray overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-hpi-light-gray text-hpi-med-gray border-b bg-gray-50 text-left text-xs font-medium tracking-wider uppercase">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Investigator</th>
                  <th className="px-4 py-2">Case</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Description</th>
                </tr>
              </thead>
              <tbody className="divide-hpi-light-gray divide-y bg-white">
                {entries.map((entry) => (
                  <EvidenceRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

/* -- Row -------------------------------------------------- */

function EvidenceRow({ entry }: { entry: EvidenceEntry }) {
  const href = entry.case_id ? caseEvidencePath(entry.case_id, entry.id) : null

  return (
    <tr className="hover:bg-hpi-pale-blue">
      <td className="px-4 py-2 whitespace-nowrap">
        <CellLink href={href}>{formatDate(entry.evidence_date)}</CellLink>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <CellLink href={href}>{entry.employee_name ?? "-"}</CellLink>
      </td>
      <td className="px-4 py-2">
        <CellLink href={href}>{entry.case_name ?? "-"}</CellLink>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <CellLink href={href}>{entry.evidence_type ?? "-"}</CellLink>
      </td>
      <td className="px-4 py-2">
        <CellLink href={href}>{entry.description}</CellLink>
      </td>
    </tr>
  )
}

/* -- Cell Link -------------------------------------------- */

function CellLink({
  href,
  children,
}: {
  href: string | null
  children: React.ReactNode
}) {
  if (!href) {
    return <span className="text-hpi-dark-gray">{children}</span>
  }
  return (
    <a href={href} className="text-hpi-blue hover:underline">
      {children}
    </a>
  )
}
